import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, AlertTriangle, Link2, ChevronLeft, ChevronRight } from "lucide-react";

const STAGE_COLORS = {
  "New": "pgm-badge",
  "Booked": "pgm-badge-amber",
  "Form Sent": "pgm-badge-orange",
  "Form Complete": "pgm-badge-magenta",
  "Promo Ready": "pgm-badge-olive",
  "Recorded": "pgm-badge-wine",
  "Published": "pgm-badge-olive",
  "Done": "pgm-badge-olive",
};

function StatCards({ stats }) {
  const cards = [
    { testid: "stat-total-guests", label: "Total guests", value: stats?.total_guests },
    { testid: "stat-confirmed", label: "Confirmed", value: stats?.confirmed_guests },
    { testid: "stat-no-shows", label: "No-shows", value: stats?.no_show_guests },
    { testid: "stat-preshow", label: "Pre-show steps done", value: stats?.pre_show_steps_done },
    { testid: "stat-postshow", label: "Post-show steps done", value: stats?.post_show_steps_done },
    { testid: "stat-stages", label: "Stages in progress", value: stats ? Object.keys(stats.stage_counts || {}).length : null },
  ];
  return (
    <div className="pgm-stat-grid pgm-reveal-stagger" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
      {cards.map(({ testid, label, value }) => (
        <div className="pgm-stat" data-testid={testid} key={testid}>
          <div className="pgm-micro">{label}</div>
          <div className="pgm-stat-value">{value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function _fmtMonth(key) {
  if (!key || key.length < 7) return key || "—";
  const [y, m] = key.split("-");
  return `${MONTH_LABELS[Number(m) - 1] || m} ${y}`;
}

function _fmtShort(key) {
  if (!key || key.length < 7) return key || "—";
  const [, m] = key.split("-");
  return MONTH_LABELS[Number(m) - 1] || m;
}

function Sparkline({ values, color, testid }) {
  const w = 220;
  const h = 44;
  const pad = 4;
  const max = Math.max(1, ...values);
  const stepX = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (v / max) * (h - pad * 2);
    return [x, y];
  });
  const path = pts.map(([x, y], i) => (i === 0 ? `M ${x},${y}` : `L ${x},${y}`)).join(" ");
  const areaPath = `${path} L ${pts[pts.length - 1]?.[0] || pad},${h - pad} L ${pts[0]?.[0] || pad},${h - pad} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-testid={testid} style={{ display: "block" }}>
      <path d={areaPath} fill={color} opacity="0.16" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 3.5 : 2} fill={color} />
      ))}
    </svg>
  );
}

function DeltaPill({ n }) {
  if (n === null || n === undefined) return null;
  const up = n > 0;
  const zero = n === 0;
  const color = zero ? "var(--pgm-magenta)" : up ? "var(--pgm-olive)" : "var(--pgm-orange)";
  const arrow = zero ? "→" : up ? "▲" : "▼";
  return (
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color, marginLeft: 6 }}>
      {arrow} {Math.abs(n)} vs last month
    </span>
  );
}

function EpisodeAnalyticsCard({ data }) {
  const months = data?.months || [];
  const cur = data?.current_month;
  const deltas = data?.deltas || {};
  const metrics = [
    { key: "guests_booked", label: "Guests booked", color: "#4E1D5E", testid: "analytics-guests" },
    { key: "forms_completed", label: "Forms completed", color: "#772F88", testid: "analytics-forms" },
    { key: "emails_sent", label: "Emails sent", color: "#A4A510", testid: "analytics-emails" },
  ];
  const hasAny = months.some((m) => (m.guests_booked || m.forms_completed || m.emails_sent));
  return (
    <section style={{ marginTop: 24 }} data-testid="analytics-card">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <h2 className="pgm-display" style={{ fontSize: 26, margin: 0 }}>Episode analytics</h2>
        <span style={{ fontSize: 12, opacity: 0.65 }}>
          {cur ? `This month — ${_fmtMonth(cur.month)}` : "—"} · last {months.length} months
        </span>
      </div>
      {!hasAny && (
        <div className="pgm-callout" style={{ marginBottom: 12, fontSize: 13 }}>
          <strong>No data yet.</strong> Book a guest, send an intake, or fire a check-in email and stats will populate here.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
        {metrics.map(({ key, label, color, testid }) => {
          const values = months.map((m) => Number(m[key] || 0));
          const bigNumber = cur ? Number(cur[key] || 0) : 0;
          return (
            <div key={key} className="pgm-card" style={{ borderLeftColor: color, padding: 16 }} data-testid={testid}>
              <div className="pgm-micro" style={{ marginBottom: 4 }}>◆ {label}</div>
              <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--pgm-serif, 'DM Serif Display', serif)", fontSize: 40, lineHeight: 1, color: "var(--pgm-ink)" }} data-testid={`${testid}-number`}>
                  {bigNumber}
                </span>
                <DeltaPill n={deltas[key]} />
              </div>
              <div style={{ marginTop: 12 }}>
                <Sparkline values={values} color={color} testid={`${testid}-spark`} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, opacity: 0.55, marginTop: 4 }}>
                <span>{_fmtShort(months[0]?.month)}</span>
                <span>{_fmtShort(cur?.month)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function BookingCalendar({ guests }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const byDate = {};
  guests.forEach(g => {
    if (!g.episode_date) return;
    (byDate[g.episode_date] = byDate[g.episode_date] || []).push(g);
  });

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const lead = first.getDay();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const iso = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === d;
  const shift = (n) => {
    const next = new Date(cursor.y, cursor.m + n, 1);
    setCursor({ y: next.getFullYear(), m: next.getMonth() });
  };
  const monthCount = Object.keys(byDate).filter(k => k.startsWith(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`))
    .reduce((n, k) => n + byDate[k].length, 0);

  return (
    <div style={{ marginTop: 28 }} data-testid="booking-calendar">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h3 className="pgm-display" style={{ fontSize: 22, margin: 0 }}>
          Booking calendar
          <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.65, marginLeft: 10 }} data-testid="calendar-month-count">
            {monthCount === 1 ? "1 episode this month" : `${monthCount} episodes this month`}
          </span>
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 9px" }} onClick={() => shift(-1)} data-testid="calendar-prev" aria-label="Previous month">
            <ChevronLeft size={14} />
          </button>
          <div className="pgm-micro" style={{ minWidth: 132, textAlign: "center", fontSize: 11 }} data-testid="calendar-label">
            {FULL_MONTHS[cursor.m]} {cursor.y}
          </div>
          <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 9px" }} onClick={() => shift(1)} data-testid="calendar-next" aria-label="Next month">
            <ChevronRight size={14} />
          </button>
          <button
            className="pgm-btn pgm-btn-ghost"
            style={{ padding: "5px 11px", fontSize: 11 }}
            onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })}
            data-testid="calendar-today"
          >
            Today
          </button>
        </div>
      </div>

      <div className="pgm-cal" style={{ border: "1.5px solid var(--pgm-rule)", background: "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "var(--pgm-wine)" }}>
          {DAY_LABELS.map(d => (
            <div key={d} className="pgm-cal-head" style={{ padding: "7px 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--pgm-cream)", textAlign: "center" }}>
              {d}
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {cells.map((d, i) => {
            const bookings = d ? (byDate[iso(d)] || []) : [];
            return (
              <div
                key={`cal-${cursor.y}-${cursor.m}-${i}`}
                className="pgm-cal-cell"
                style={{
                  minHeight: 88,
                  padding: 6,
                  borderRight: (i + 1) % 7 === 0 ? "none" : "1px solid var(--pgm-rule)",
                  borderTop: "1px solid var(--pgm-rule)",
                  background: !d ? "rgba(78,29,94,0.04)" : isToday(d) ? "rgba(214,51,132,0.07)" : "transparent",
                  boxShadow: d && isToday(d) ? "inset 0 0 0 2px var(--pgm-magenta)" : "none",
                }}
                data-testid={d ? `calendar-day-${iso(d)}` : undefined}
              >
                {d && (
                  <div style={{ fontSize: 11, fontWeight: 700, opacity: isToday(d) ? 1 : 0.55, color: isToday(d) ? "var(--pgm-magenta)" : "var(--pgm-wine)", marginBottom: 4 }}>
                    {d}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {bookings.map(g => (
                    <Link
                      key={g.id}
                      to={`/guests/${g.id}`}
                      title={`${g.full_name || "Untitled guest"}${g.episode_time ? ` · ${g.episode_time}` : ""}${g.cancelled ? " · cancelled" : ""}`}
                      style={{
                        display: "block",
                        textDecoration: g.cancelled ? "line-through" : "none",
                        background: g.cancelled ? "rgba(78,29,94,0.12)" : "var(--pgm-olive)",
                        color: g.cancelled ? "var(--pgm-wine)" : "var(--pgm-cream)",
                        fontSize: 11,
                        lineHeight: 1.25,
                        padding: "3px 5px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        opacity: g.cancelled ? 0.65 : 1,
                      }}
                      data-testid={`calendar-booking-${g.id}`}
                    >
                      {g.episode_time ? `${g.episode_time} ` : ""}{g.full_name || "Untitled"}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RecentGuests({ recent, allGuests }) {
  return (
    <section>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <h2 className="pgm-display" style={{ fontSize: 26 }}>Recent guests</h2>
        <Link to="/guests" className="pgm-link" data-testid="dashboard-view-all-guests">View all →</Link>
      </div>
      {recent.length === 0 ? (
        <div className="pgm-callout">
          <strong>No guests yet.</strong> Head to the Guests page to add your first booking.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {recent.map(g => (
            <Link key={g.id} to={`/guests/${g.id}`} className="pgm-card" style={{ textDecoration: "none", display: "block" }} data-testid={`dashboard-guest-${g.id}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div className="pgm-display" style={{ fontSize: 20 }}>{g.full_name || "Untitled"}</div>
                  <div style={{ fontSize: 12, color: "var(--pgm-wine)", opacity: 0.7 }}>{g.title || "—"} · Episode {g.episode_number || "TBD"} · {g.episode_date || "Date TBD"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span className={`pgm-badge ${STAGE_COLORS[g.stage] || "pgm-badge"}`}>{g.stage}</span>
                  <ArrowRight size={18} color="var(--pgm-magenta)" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <BookingCalendar guests={allGuests} />
    </section>
  );
}

function StageBreakdown({ stats }) {
  const entries = Object.entries(stats?.stage_counts || {});
  return (
    <>
      <h2 className="pgm-display" style={{ fontSize: 26, marginBottom: 14 }}>By stage</h2>
      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)" }}>
        {stats && entries.length === 0 && <em style={{ opacity: 0.6 }}>No data yet.</em>}
        {entries.map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--pgm-rule)" }}>
            <span className="pgm-micro" style={{ letterSpacing: "0.1em" }}>{k}</span>
            <span className="pgm-display" style={{ fontSize: 20 }}>{v}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function AutomationActivity({ activity }) {
  return (
    <>
      <h2 className="pgm-display" style={{ fontSize: 26, margin: "24px 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <Zap size={20} color="var(--pgm-magenta)" /> Automation activity
      </h2>
      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)" }} data-testid="automation-activity">
        {activity.length === 0 ? (
          <em style={{ opacity: 0.6, fontSize: 13 }}>No automated sends yet — confirmations, reminders and pre-show check-ins will appear here.</em>
        ) : (
          activity.map(l => (
            <div key={l.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--pgm-rule)" }} data-testid={`activity-${l.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--pgm-wine)" }}>{l.template_label}</span>
                <span className={`pgm-badge ${l.sent ? "pgm-badge-olive" : "pgm-badge-orange"}`} style={{ fontSize: 10 }}>{l.sent ? "Sent" : "Failed"}</span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                {l.guest_name || "Unknown guest"} · {l.to}
              </div>
              <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>
                {l.created_at ? new Date(l.created_at).toLocaleString() : ""}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
function MissingBroadcastLinksAlert({ items }) {
  if (!items || items.length === 0) return null;
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch (e) { return iso; }
  };
  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 24 }} data-testid="dashboard-missing-broadcast">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <AlertTriangle size={17} color="var(--pgm-orange)" />
        <h3 className="pgm-display" style={{ fontSize: 20, margin: 0, color: "var(--pgm-orange)" }}>
          {items.length === 1 ? "1 guest is missing a studio link" : `${items.length} guests are missing a studio link`}
        </h3>
      </div>
      <p style={{ fontSize: 13, opacity: 0.8, margin: "0 0 12px 0" }}>
        The pre-show email won&rsquo;t send until each of these guests has a StreamYard, Zoom, Restream (or similar) link on their Info tab. Set one now so your guests know where to join.
      </p>
      <p style={{ fontSize: 12, opacity: 0.65, margin: "0 0 12px 0", fontStyle: "italic" }}>
        We&rsquo;ll also email you a reminder within 6 hours (and again, marked URGENT, when the episode is 2 days away) until the link is set.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {items.map(g => (
          <Link
            key={g.id}
            to={`/guests/${g.id}`}
            className="pgm-card"
            style={{ borderLeftColor: "var(--pgm-orange)", padding: 12, textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}
            data-testid={`dashboard-missing-broadcast-item-${g.id}`}
          >
            <Link2 size={14} color="var(--pgm-orange)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "var(--pgm-wine)", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.full_name || "Untitled guest"}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{g.episode_number ? `${g.episode_number} • ` : ""}{fmt(g.episode_date)}</div>
            </div>
            <ArrowRight size={13} color="var(--pgm-wine)" />
          </Link>
        ))}
      </div>
    </div>
  );
}



export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const [s, g, a, m] = await Promise.all([
      api.get("/dashboard"),
      api.get("/guests"),
      api.get("/automation-activity"),
      api.get("/analytics/monthly?months=6"),
    ]);
    setStats(s.data);
    setRecent(g.data.slice(0, 6));
    setAllGuests(g.data);
    setActivity(a.data);
    setAnalytics(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Overview</div>
      <h1 className="pgm-display" style={{ fontSize: 44, marginTop: 4, marginBottom: 4 }}>
        {stats?.show_name || "Your Show"}
      </h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 28 }}>
        Every guest touchpoint — confirmation, intake, promo, post-episode — managed from one place.
      </p>

      <StatCards stats={stats} />

      <MissingBroadcastLinksAlert items={stats?.missing_broadcast_links || []} />

      {analytics && <EpisodeAnalyticsCard data={analytics} />}

      <div className="pgm-divider">◆</div>

      <div className="pgm-dashboard-columns" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
        <RecentGuests recent={recent} allGuests={allGuests} />
        <aside>
          <StageBreakdown stats={stats} />
          <AutomationActivity activity={activity} />
        </aside>
      </div>
    </div>
  );
}
